/** @jsxImportSource @emotion/react */
import { FC, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { Buttons } from '@zip/business-components';
import { useMerchantApplicationContext, useSnackbar } from 'contexts';
import { EmailVerification } from 'public/illustrations';
import { isFormError } from 'types/common/ApiHelpers';
import { useSignUpFunctions } from 'utils';
import * as styles from './VerifyEmailModule.styles';

const LOGIN_SECTION_DELAY_MS = 60000;

export const VerifyEmailModule: FC = () => {
  const Snackbar = useSnackbar();
  const { getStepField, merchantApplicationId } =
    useMerchantApplicationContext();
  const { resendVerificationEmailAsync, mapServerError } = useSignUpFunctions();
  const [isResending, setIsResending] = useState(false);
  const [isLoginSectionVisible, setIsLoginSectionVisible] = useState(false);
  const businessEmail =
    (getStepField('signUpInfo', 'businessEmail') as string | undefined) ||
    'your email address';

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoginSectionVisible(true);
    }, LOGIN_SECTION_DELAY_MS);

    return () => clearTimeout(timer);
  }, []);

  const handleResendEmail = async () => {
    if (!merchantApplicationId || isResending) {
      return;
    }

    setIsResending(true);

    try {
      await resendVerificationEmailAsync(merchantApplicationId);
      Snackbar.success('Verification email sent');
    } catch (err) {
      const mapped = mapServerError(err);
      if (isFormError(mapped)) {
        Snackbar.error(mapped.message);
      } else {
        Snackbar.error('Unable to resend verification email');
      }
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Box css={styles.container}>
      <img
        src={EmailVerification}
        alt=""
        width={96}
        height={96}
        css={styles.icon}
      />

      <Box css={styles.content}>
        <h1 className="heading1" css={styles.title}>
          Verify your email
        </h1>
        <p className="body2" css={styles.body}>
          We have sent a verification email to{' '}
          <strong className="fs-mask">{businessEmail}</strong>. Please click on
          the link to verify your account and continue to the login page.
        </p>

        <p className="body2" css={styles.resendRow}>
          Didn&apos;t receive an email?{' '}
          <button
            type="button"
            className="body2"
            css={styles.inlineButton}
            onClick={handleResendEmail}
            disabled={isResending}
          >
            Resend email
          </button>
        </p>

        {isLoginSectionVisible && (
          <>
            <Box css={styles.divider} />

            <p className="body2" css={styles.secondaryBody}>
              If you have verified your account, you can now login to the
              dashboard.
            </p>

            <Buttons.Primary
              variant="contained"
              css={styles.primaryButton}
              onClick={() => {
                window.location.href = window.location.origin;
              }}
            >
              Log in
            </Buttons.Primary>
          </>
        )}
      </Box>
    </Box>
  );
};

export default VerifyEmailModule;
